import {
  Button, Card, Spinner, Text, Title3, makeStyles, mergeClasses, shorthands, tokens,
} from '@fluentui/react-components';
import { ArrowLeft24Regular } from '@fluentui/react-icons';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmationDialog } from '../components/Common/Dialogs';
import { SettingToggle } from '../components/Common/SettingToggle';
import { SettingsBanner } from '../components/Common/SettingsBanner';
import { useSavingGuard } from '../hooks/useSavingGuard';
import { useScrollableMessage } from '../hooks/useScrollableMessage';
import { policyService } from '../services/policyService';
import { useCommonSettingsStyles } from './commonSettingsStyles';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', ...shorthands.gap('24px') },
  card: { ...shorthands.padding('24px'), display: 'flex', flexDirection: 'column', ...shorthands.gap('24px') },
  sectionTitle: { color: tokens.colorBrandForeground1, fontSize: '20px' },
  header: { marginBottom: 0 },
  desc: { fontSize: '14px', color: '#000000' },
});

type DialogKind = 'bilateralOn' | null;

export const EditPolicy: React.FC = () => {
  const styles = useStyles();
  const common = useCommonSettingsStyles();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [bilateralChat, setBilateralChat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [savingField, setSavingField] = useState<'bilateral' | null>(null);
  const [success, setSuccess] = useScrollableMessage<string | null>(null);
  const [error, setError] = useScrollableMessage<string | null>(null);

  const saving = savingField !== null;
  const { requestNavigation } = useSavingGuard(saving);

  useEffect(() => {
    if (!id) return;
    policyService
      .getPolicyById(id)
      .then((policy) => {
        if (policy) {
          setBilateralChat(
            policy.federatedBilateralChats ??
              policy.FederatedBilateralChats ??
              policy.bilateralChat ??
              false,
          );
        }
      })
      .catch(() => setError('There was a problem. Try again later.'))
      .finally(() => setLoading(false));
  }, [id, setError]);

  const persist = async (
    field: 'bilateral',
    patch: { bilateralChat?: boolean },
    apply: () => void,
    revert: () => void,
  ) => {
    if (!id) return;
    setSavingField(field);
    try {
      await policyService.updatePolicySettings(id, patch);
      apply();
      setSuccess('Settings updated');
      setError(null);
    } catch {
      revert();
      setError('There was a problem. Try again later.');
      setSuccess(null);
    } finally {
      setSavingField(null);
    }
  };

  const enableBilateral = () => setDialog('bilateralOn');
  const disableBilateral = () =>
    void persist('bilateral', { bilateralChat: false }, () => setBilateralChat(false), () => setBilateralChat(true));

  const confirmDialog = async () => {
    const kind = dialog;
    setDialog(null);
    if (kind === 'bilateralOn') {
      await persist('bilateral', { bilateralChat: true }, () => setBilateralChat(true), () => setBilateralChat(false));
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner size="large" label="Loading policy..." data-testid="edit-policy-loading-spinner" />
      </div>
    );
  }

  const dialogCopy = {
    bilateralOn: {
      title: 'Apply bilateral chat limit?',
      message:
        'This change applies to users assigned to this policy. Existing external group chats that include more than two organisations will be disabled. Do you want to continue?',
    },
  };

  return (
    <div className={styles.page} data-testid="edit-policy-page">
      <div className={mergeClasses(common.header, styles.header)}>
        <Text className={common.title}>
          <Button appearance="subtle" icon={<ArrowLeft24Regular />}
            onClick={() => requestNavigation(() => navigate(`/policies/${id}`))} data-testid="edit-policy-back-button">
            Back
          </Button>
          Edit Policy
        </Text>
      </div>

      {error && (
        <SettingsBanner intent="error" message={error} onDismiss={() => setError(null)}
          barTestId="edit-policy-error-bar" dismissTestId="edit-policy-error-dismiss" />
      )}
      {success && (
        <SettingsBanner intent="success" message={success} onDismiss={() => setSuccess(null)}
          barTestId="edit-policy-success-bar" dismissTestId="edit-policy-success-dismiss" />
      )}

      <Card className={styles.card}>
        <Title3 className={styles.sectionTitle}>Edit Policy Details</Title3>
        <Text className={styles.desc}>
          Control how users assigned to this policy communicate with external organisations.
        </Text>

        <SettingToggle
          label="Enable bilateral chat (limit new external group chats to two organisations)"
          checked={bilateralChat}
          saving={savingField === 'bilateral'}
          disabled={savingField !== null}
          onChange={(checked) => (checked ? enableBilateral() : disableBilateral())}
          switchTestId="edit-policy-bilateral-switch"
          savingTestId="edit-policy-bilateral-saving"
          description={
            <>
              Limits new group chats with Teams and Skype for Business users from trusted organisations to two
              organisations — yours plus one external. Existing external group chats with more than two organisations
              are disabled for users and groups assigned to this policy.
            </>
          }
        />
      </Card>

      <ConfirmationDialog
        open={dialog !== null}
        onClose={() => setDialog(null)}
        onConfirm={confirmDialog}
        title={dialog ? dialogCopy[dialog].title : ''}
        message={dialog ? dialogCopy[dialog].message : ''}
      />
    </div>
  );
};
